import * as amplitude from "@amplitude/analytics-browser"
import { useEffect } from "react"
import { getFragmentData } from "~/__generated__"
import { useCurrentUserMaybe } from "~/auth/CurrentUserContext"
import { CURRENT_SUBSCRIPTION_FRAGMENT } from "~/auth/currentUserQuery"
import { isAmplitudeEnabled } from "~/util/amplitude"

// Attaches the signed-in user to Amplitude events, so sessions can be followed
// across devices and events can be segmented by agency and plan.
export const useAmplitudeUser = () => {
  const { currentUser } = useCurrentUserMaybe()

  useEffect(() => {
    if (!isAmplitudeEnabled()) return

    if (!currentUser) {
      // Signed out: drop the user id and device id so a subsequent session on
      // this browser isn't attributed to whoever was signed in before.
      if (amplitude.getUserId()) {
        amplitude.reset()
      }
      return
    }

    amplitude.setUserId(currentUser.id)

    const identify = new amplitude.Identify()
    identify.set("email", currentUser.email ?? "")
    identify.set("name", `${currentUser.firstName} ${currentUser.lastName}`)
    identify.set("jobTitle", currentUser.jobTitle ?? "")
    identify.set("isAdmin", currentUser.isAdmin)
    identify.set("isOnboarded", currentUser.isOnboarded)
    identify.set("createdAt", currentUser.createdAt)

    if (currentUser.agency) {
      identify.set("agencyId", currentUser.agency.id)
      identify.set("agencyName", currentUser.agency.name)
      identify.set("agencyBusinessType", currentUser.agency.businessType ?? "")
      identify.set("agencyRole", currentUser.agencyRole ?? "")
    }

    const subscription = getFragmentData(
      CURRENT_SUBSCRIPTION_FRAGMENT,
      currentUser.activeSubscription
    )
    identify.set("subscriptionTier", subscription?.tier?.key ?? "none")
    identify.set(
      "subscriptionBillingCycle",
      subscription?.tier?.billingCycle ?? ""
    )
    identify.set("subscriptionStatus", subscription?.status ?? "none")

    amplitude.identify(identify)
  }, [currentUser])
}
